import {MODULE_CONFIG} from "../config";
import {AppClientTokens} from "../types.ts";
import {
    HANDOFF_ERROR_CODES,
    HandoffError
} from "./HandoffError.ts";

type AuthenticationResult = {
    IdToken?: string,
    AccessToken?: string,
    RefreshToken?: string,
};

type InitiateAuthResponse = {
    ChallengeName?: string,
    ChallengeParameters?: { [key: string]: string },
    Session?: string,
    AuthenticationResult?: AuthenticationResult,
};

const callIdentityProvider = async <T>(operation: string, body: object): Promise<T> => {
    const response = await fetch(MODULE_CONFIG.cognitoIdpEndpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': `AWSCognitoIdentityProviderService.${operation}`,
        },
        body: JSON.stringify(body),
    });

    const data = await response.json()
                                .catch(() => ({}));

    if (!response.ok) {
        const type = typeof data.__type === 'string' ? data.__type.split('#')
                                                                  .pop() : 'UnknownError';
        throw new HandoffError(type, data.message);
    }

    return data as T;
};

export const refreshAccessToken = async (clientId: string, refreshToken: string) => {
    const response = await callIdentityProvider<InitiateAuthResponse>('InitiateAuth', {
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: clientId,
        AuthParameters: {REFRESH_TOKEN: refreshToken},
    });

    const result = response.AuthenticationResult;
    if (!result?.AccessToken) {
        throw new HandoffError(HANDOFF_ERROR_CODES.invalidRefreshResponse);
    }

    return {
        accessToken: result.AccessToken,
        refreshToken: result.RefreshToken ?? refreshToken,
    };
};

export const signInWithPlatformSession = async (appClientId: string, username: string, platformAccessToken: string): Promise<AppClientTokens> => {
    const challenge = await callIdentityProvider<InitiateAuthResponse>('InitiateAuth', {
        AuthFlow: 'CUSTOM_AUTH',
        ClientId: appClientId,
        AuthParameters: {USERNAME: username},
    });

    if (challenge.ChallengeName !== 'CUSTOM_CHALLENGE' ||
        challenge.ChallengeParameters?.challenge !== MODULE_CONFIG.platformSessionChallenge ||
        !challenge.Session) {
        throw new HandoffError(HANDOFF_ERROR_CODES.unexpectedChallenge);
    }

    const response = await callIdentityProvider<InitiateAuthResponse>('RespondToAuthChallenge', {
        ClientId: appClientId,
        ChallengeName: 'CUSTOM_CHALLENGE',
        Session: challenge.Session,
        ChallengeResponses: {
            USERNAME: challenge.ChallengeParameters.USERNAME ?? username,
            ANSWER: platformAccessToken,
        },
    });

    const result = response.AuthenticationResult;
    if (!result?.IdToken || !result.AccessToken || !result.RefreshToken) {
        throw new HandoffError(HANDOFF_ERROR_CODES.unexpectedChallengeResponse);
    }

    return {
        idToken: result.IdToken,
        accessToken: result.AccessToken,
        refreshToken: result.RefreshToken,
    };
};
